import { db, functions } from '../firebase'
import { deleteField, doc, getDoc, runTransaction, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

import type { User } from '../types/user.types'
import type { ChangeClubTeamMemberArgs, ChangeClubTeamMemberResponse, FirebaseError, UsersMapType } from './admin.types'
import { apiSlice } from './apiSlice'

export const adminApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({

    // Fetch admin
    fetchAdmin: builder.query({
      async queryFn() {
        try {
          const adminDoc = await getDoc(doc(db, 'admin', 'metadata'))

          if (adminDoc.exists()) {
            const adminData = adminDoc.data()

            return { data: adminData }
          } else {
            throw new Error('No admin document found')
          }

        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      providesTags: [{ type: 'Admin' }]
    }),

    // Fetch admin notifications
    fetchAdminNotifications: builder.query({
      async queryFn() {
        try {
          const [messagesDoc, requestsDoc] = await Promise.all([
            getDoc(doc(db, 'admin', 'messages')),
            getDoc(doc(db, 'admin', 'requests'))
          ])

          if (!messagesDoc.exists() || !requestsDoc.exists()) {
            throw new Error('Error fetching admin notifications')
          }

          const notificationsMap = {
            messages: messagesDoc.data() || {},
            requests: requestsDoc.data() || {}
          }

          return { data: notificationsMap }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      providesTags: [{ type: 'Notifications', id: 'Admin' }]
    }),

    // Clear admin notifications
    clearAdminNotifications: builder.mutation({
      async queryFn(_, _queryApi) {
        // Update the cached data for usersSummary in RTK Query
        _queryApi.dispatch(
          adminApiSlice.util.updateQueryData('fetchAdmin', undefined, (draft) => {
            if (draft) {
              draft.notifications = 0
            }
          })
        )

        try {
          await updateDoc(doc(db, 'admin', 'metadata'), {
            notifications: 0
          })

          return { data: 'Notifications cleared' }
        } catch (error) {
          console.log(error)
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message
              }
            },
          }
        }
      }
    }),

    // Delete admin notification
    deleteAdminNotification: builder.mutation({
      async queryFn({ docId, category }) {
        try {
          if (!docId || !category) {
            console.log("All data is not provided")
            throw new Error("Correct data is not provided")
          }

          await updateDoc(doc(db, 'admin', category), {
            [`${ docId }`]: deleteField()
          })

          return { data: 'Notification deleted' }
        } catch (error) {
          console.log(error)
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: { message: err.message }
            }
          }
        }
      },
      async onQueryStarted({ docId, category }, { dispatch, queryFulfilled }) {
        dispatch(
          // @ts-expect-error - Optimistic update for cache
          apiSlice.util.updateQueryData("fetchAdminNotifications", undefined, (draft: any) => {
            if (draft?.[category]?.[docId]) {
              delete draft[category][docId]
            }
          }),
        )
        try {
          await queryFulfilled
        } catch (error) {
          dispatch(apiSlice.util.invalidateTags(["Notifications"]))
        }
      }
    }),

    // Mark all admin notifications as read
    adminNotificationsRead: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'adminNotificationsRead')
          await callable(data)

          return { data: 'Notifications marked as read' }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message
              }
            },
          }
        }
      },
      async onQueryStarted({ requests, messages }, { dispatch, queryFulfilled }) {
        // Optimistic update
        dispatch(
          // @ts-expect-error - Optimistic update for cache
          apiSlice.util.updateQueryData("fetchAdminNotifications", undefined, (draft: any) => {
            if (!draft) return

            // Mark all specified request IDs as read
            requests.forEach((id) => {
              if (draft.requests?.[id]) {
                draft.requests[id].read = true
              }
            })

            // Mark all specified message IDs as read
            messages.forEach((id) => {
              if (draft.messages?.[id]) {
                draft.messages[id].read = true
              }
            })
          })
        )

        try {
          await queryFulfilled
        } catch (error) {
          // Refetch fresh data if update fails
          dispatch(apiSlice.util.invalidateTags(["Notifications"]))
        }
      }
    }),

    // Fetch all users
    fetchUsers: builder.query<UsersMapType, void>({
      async queryFn() {
        try {
          const querySnapshot = await getDoc(doc(db, 'usersSummary', 'usersMap'))

          if (querySnapshot.exists()) {
            const usersMap = querySnapshot.data() as UsersMapType

            return { data: usersMap }
          } else {
            return { data: {} as UsersMapType }
          }

        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      providesTags: (result, error, arg) => [{ type: 'Users' }]
    }),

    // Fetch selected user data by ID
    fetchUserById: builder.query({
      async queryFn(id) {
        try {
          if (!id) {
            throw new Error("Id is not provided")
          }

          const userDoc = await getDoc(doc(db, 'users', id))

          if (userDoc.exists()) {
            const userData = userDoc.data() as User

            const clubsArray = Object.entries(userData.clubs).map(([clubId, club]) => {
              return {
                clubId,
                clubName: club.clubName,
                positions: Object.keys(club.positions || {})
              }
            })
            return { data: { ...userData, uid: id, clubs: clubsArray } }
          } else {
            throw new Error('No user document found')
          }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      providesTags: (result, error, id) => [{ type: 'User', id }]
    }),

    // Change club team member
    changeClubTeamMember: builder.mutation<ChangeClubTeamMemberResponse, ChangeClubTeamMemberArgs>({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'changeClubTeamMember')
          const response = await callable(data)

          return { data: response.data as ChangeClubTeamMemberResponse }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'User', id: arg.userId },
        { type: 'User', id: result?.removedUserId },
        { type: "Club", id: arg.clubId },
        { type: "ClubMembers", id: arg.clubId },
        { type: "Clubs" }
      ]
    }),

    // Migrate user account
    migrateAccount: builder.mutation({
      async queryFn({ createAccountData, completeMembershipData }: any) {
        try {
          const createAccountCallable = httpsCallable(functions, "createAccount")
          const result = await createAccountCallable(createAccountData)
          const { uid } = result.data as any

          const completeMembershipCallable = httpsCallable(functions, "completeMemberDetails")
          await completeMembershipCallable({ uid, ...completeMembershipData })

          return {
            data: {
              message: "Account created successfully",
              uid,
              name: `${ completeMembershipData.firstName } ${ completeMembershipData.lastName }`,
              email: createAccountData.email
            }
          }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      async onQueryStarted(updateData: any, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
            apiSlice.util.updateQueryData('fetchUsers', undefined, (draft: any) => {
              // update the usersSummary adding date a year from now to satisfy validUntilCheck instead of fetching all users data again
              if (draft) {
                draft[data.uid] = {
                  email: data.email,
                  disabled: false,
                  name: data.name,
                  memberDetailsComplete: true,
                  validUntil: Date.now() + (365 * 24 * 60 * 60 * 1000) // Timestamp a year from now
                }
              }
            })
          )

          const clubNumber = updateData?.completeMembershipData?.clubNumber

          if (clubNumber) {
            dispatch(
              // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
              apiSlice.util.updateQueryData("fetchClubs", undefined, (draft: any) => {
                if (draft?.[clubNumber]?.membersCount) {
                  draft[clubNumber].membersCount += 1
                }
              })
            )
          }
        } catch {
          // Query failed, don't update cache
        }
      },
      invalidatesTags: (_, __, { completeMembershipData }) =>
        completeMembershipData?.clubNumber ? [{ type: 'Club', id: completeMembershipData.clubNumber }] : []
    }),

    // Create user account
    adminCreateAccount: builder.mutation({
      async queryFn(data) {
        try {
          const createAccountCallable = httpsCallable(functions, "createAccount")
          const result = await createAccountCallable(data)

          return {
            data: { message: "Account created successfully", uid: (result.data as any).uid, email: data.email }
          }
        } catch (error) {
          const err = error as FirebaseError
          console.error("Error creating account:", err.code || 500, err.message)
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      async onQueryStarted(updateData: any, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
            apiSlice.util.updateQueryData('fetchUsers', undefined, (draft: any) => {
              if (draft) {
                draft[data.uid] = {
                  email: data.email,
                  disabled: false,
                  memberDetailsComplete: false
                }
              }
            })
          )

          const clubNumber = updateData?.completeMembershipData?.clubNumber

          if (clubNumber) {
            dispatch(
              // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
              apiSlice.util.updateQueryData("fetchClubs", undefined, (draft: any) => {
                if (draft?.[clubNumber]?.membersCount) {
                  draft[clubNumber].membersCount += 1
                }
              })
            )
          }
        } catch {
          // Query failed, don't update cache
        }
      },
    }),

    // Disable user
    disableUser: builder.mutation({
      async queryFn({ id: uid, disabled, ...rest }, _queryApi) {

        const userRef = doc(db, "users", uid)
        const usersSummaryRef = doc(db, "usersSummary", "usersMap")

        try {
          await runTransaction(db, async (transaction) => {

            // Update the user's disabled status in users profile
            transaction.set(userRef, { disabled: !disabled }, { merge: true })

            // Update the user's disabled status in usersSummary

            transaction.update(usersSummaryRef, {
              [`${ uid }.disabled`]: !disabled
            })

          })

          // Update the cached data for usersSummary in RTK Query
          _queryApi.dispatch(
            // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
            apiSlice.util.updateQueryData('fetchUsers', undefined, (draft: any) => {
              if (draft[uid]) {
                draft[uid] = {
                  ...draft[uid],
                  disabled: !draft[uid].disabled,
                }
              }
            })
          )

          return { data: 'User disabled successfully' }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      }
    }),

    markUserPaid: builder.mutation({
      async queryFn({ id: userId }) {
        try {
          const callable = httpsCallable<{ userId: string }, { message: string; validUntil: string }>(functions, 'markUserPaid')
          const result = await callable({ userId })
          return { data: result.data }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: { message: err.message },
            },
          }
        }
      },
      async onQueryStarted({ id: userId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (!data?.validUntil) return

          dispatch(
            // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
            apiSlice.util.updateQueryData('fetchUserById', userId, (draft: User) => {
              draft.membership.initialPaymentComplete = true
              draft.membership.validUntil = data.validUntil
            })
          )

          // Update individual user cache
          dispatch(
            // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
            apiSlice.util.updateQueryData('fetchUsers', undefined, (draft: UsersMapType) => {
              if (draft[userId]) {
                draft[userId].validUntil = data.validUntil
              }
            })
          )
        } catch {
          // mutation failed, no update needed
        }
      },
    }),
  })
})

export const {
  useFetchAdminQuery,
  useFetchAdminNotificationsQuery,
  useClearAdminNotificationsMutation,
  useDeleteAdminNotificationMutation,
  useAdminNotificationsReadMutation,
  useFetchUsersQuery,
  useFetchUserByIdQuery,
  useChangeClubTeamMemberMutation,
  useAdminCreateAccountMutation,
  useMigrateAccountMutation,
  useDisableUserMutation,
  useMarkUserPaidMutation
} = adminApiSlice